import { useState, useCallback } from 'react';
import Sidebar from './components/layout/Sidebar';
import Topbar from './components/layout/Topbar';
import ToastStack from './components/common/ToastStack';
import SettingsModal from './components/modals/SettingsModal';
import UploadModal from './components/modals/UploadModal';
import DriveScanModal from './components/modals/DriveScanModal';
import InvoiceListPage from './pages/InvoiceListPage';
import LedgerPage from './pages/LedgerPage';
import InvoiceEditorPage from './pages/InvoiceEditorPage';
import InvoiceViewPage from './pages/InvoiceViewPage';
import DriveJobsPage from './pages/DriveJobsPage';
import LoginPage from './pages/auth/Login';

import { useToasts } from './hooks/useToasts';
import { useInvoices } from './hooks/useInvoices';
import { useStats } from './hooks/useStats';
import { useSettings } from './hooks/useSettings';

import { api } from './services/apiClient';
import { todayStr } from './utils/formatters';
import { useAuth } from './hooks/useAuth';

export default function App() {
  const { user, loading, logout } = useAuth();
  
  const [tab, setTab] = useState('sale');
  const [view, setView] = useState('list'); // list | editor | invoice-view | drive-jobs
  const [activeInv, setActiveInv] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [showDriveScan, setShowDriveScan] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);

  const { toasts, toast, removeToast } = useToasts();
  const { settings, setSettings, apiOk, dailyLimit } = useSettings();
  const { stats, loadStats } = useStats();
  const effectiveDailyLimit = stats.dailyLimit || { min: dailyLimit, max: dailyLimit * 1.15 };
  const { invoices, loading: loadingInv, invSeq, loadInvoices, createOrUpdate, remove } = useInvoices(tab, toast);

  const todaySalesAll = invoices
    .filter(i => i.type === 'sale' && i.date === todayStr())
    .reduce((s, i) => s + (i.grandTotal || 0), 0);

  const todaySalesExcl = activeInv?._id
    ? invoices
      .filter(i => i.type === 'sale' && i.date === todayStr() && i._id !== activeInv._id)
      .reduce((s, i) => s + (i.grandTotal || 0), 0)
    : todaySalesAll;

  function openNew() {
    const prefix = tab === 'sale' ? 'INV-S' : 'INV-P';
    const invoiceNo = `${prefix}-${String(invSeq).padStart(4, '0')}`;
    setActiveInv({
      invoiceNo,
      date: todayStr(),
      type: tab,
      items: [{ description: '', qty: 1, unitPrice: 0, vatRate: 0, total: 0 }],
      delivery: 0,
    });
    setView('editor');
  }

  async function handleSaveInvoice(payload) {
    try {
      const result = await createOrUpdate(activeInv, payload);
      toast(`Invoice ${result.invoice?.invoiceNo} saved & synced to Drive ✓`, 'success');
      loadStats();
      setView('list');
      setActiveInv(null);
      return result;
    } catch (e) {
      toast('Save failed: ' + e.message, 'error');
      throw e;
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this invoice? It will also be removed from Google Drive.')) return;
    try {
      await remove(id);
      toast('Deleted', 'success');
      loadStats();
      setView('list');
      setActiveInv(null);
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (e) {
      toast('Delete failed: ' + e.message, 'error');
    }
  }

  async function handleDeleteSubInvoice(invoiceId, subIndex) {
    if (!window.confirm('Delete this sub-invoice? It will also be removed from Google Drive.')) return;
    try {
      const result = await api.deleteSubInvoice(invoiceId, subIndex);
      toast('Sub-invoice deleted successfully', 'success');
      setActiveInv(prev => ({
        ...prev,
        subInvoices: result.subInvoices,
      }));
      loadInvoices();
      loadStats();
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (e) {
      toast('Delete failed: ' + e.message, 'error');
    }
  }

  async function handleDeleteAllSubInvoices(invoiceId) {
    if (!window.confirm('Delete all sub-invoices? They will also be removed from Google Drive.')) return;
    try {
      const result = await api.deleteAllSubInvoices(invoiceId);
      toast('All sub-invoices deleted successfully', 'success');
      setActiveInv(prev => ({
        ...prev,
        subInvoices: result.subInvoices,
      }));
      loadInvoices();
      loadStats();
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (e) {
      toast('Delete failed: ' + e.message, 'error');
    }
  }

  function handleOpen(inv) {
    setActiveInv(inv);
    setView('invoice-view');
  }

  async function handleDeleteAllInvoices() {
    const label = tab === 'sale' ? 'ALL sale invoices' : 'ALL purchase invoices';
    const ok = window.confirm(
      `This will permanently delete ${label} from the database AND their files from Google Drive. This cannot be undone.\n\nContinue?`
    );
    if (!ok) return;

    setDeletingAll(true);
    toast('Deleting all invoices — this can take a while for large amounts...', 'info');
    try {
      const result = await api.deleteAll(tab);
      toast(`Deleted ${result.deleted} of ${result.total} invoice(s)`, 'success');
    } catch (e) {
      console.error('[Delete All] failed:', e);
      toast('Delete all failed: ' + e.message, 'error');
    } finally {
      setDeletingAll(false);
      loadInvoices();
      loadStats();
    }
  }

  function backToList() {
    setView('list');
    setActiveInv(null);
  }

  const viewTitle =
    view === 'editor' ? (activeInv?._id ? 'Edit Invoice' : 'New Invoice')
      : view === 'invoice-view' ? activeInv?.invoiceNo
        : view === 'drive-jobs' ? 'Drive Jobs'
          : view === 'sales-ledger' ? 'Sales Ledger'
            : view === 'purchase-ledger' ? 'Purchase Ledger'
              : (tab === 'sale' ? 'Sales Invoices' : 'Purchase Invoices');

  if (loading) {
    return <div>Loading...</div>;
  }
  if (!user) return <LoginPage />;
  
   return (
    <div className="app-shell">
      <Sidebar
        tab={tab}
        setTab={setTab}
        view={view}
        stats={stats}
        dailyLimit={effectiveDailyLimit}
        apiOk={apiOk}
        setView={setView}
        onSettings={() => setShowSettings(true)}
        user={user}
        onLogout={logout}
      />

      <div className="main">
        <Topbar
          tab={tab}
          view={view}
          viewTitle={viewTitle}
          onBreadcrumbClick={backToList}
          onScanDrive={() => setShowDriveScan(true)}
          onUploadReceipts={() => setShowUpload(true)}
          onNewInvoice={openNew}
          onDownloadAll={() => window.open(api.bulkDownloadUrl(tab), '_blank')}
          onDeleteAll={handleDeleteAllInvoices}
          deletingAll={deletingAll}
        />

        <div className="content-area">
          {view === 'list' && (
            <InvoiceListPage
              tab={tab}
              stats={stats}
              dailyLimit={effectiveDailyLimit}
              invoices={invoices}
              loading={loadingInv}
              onOpen={handleOpen}
              onDelete={handleDelete}
              onDownload={(id) => window.open(api.xlsxUrl(id), '_blank')}
            />
          )}

          {view === 'editor' && (
            <InvoiceEditorPage
              activeInvoice={activeInv}
              tab={tab}
              dailyLimit={effectiveDailyLimit}
              todaySalesExcludingSelf={todaySalesExcl}
              saleDocumentTitle={settings.saleDocumentTitle}
              onSave={handleSaveInvoice}
              onCancel={backToList}
            />
          )}

          {view === 'invoice-view' && (
            <InvoiceViewPage
              activeInvoice={activeInv}
              saleDocumentTitle={settings.saleDocumentTitle}
              onEdit={() => setView('editor')}
              onBack={backToList}
              onDownload={() => window.open(api.xlsxUrl(activeInv._id), '_blank')}
              onDelete={handleDelete}
              onDeleteSubInvoice={handleDeleteSubInvoice}
              onDeleteAllSubInvoices={handleDeleteAllSubInvoices}
            />
          )}

          {view === 'drive-jobs' && <DriveJobsPage toast={toast} />}

          {view === 'sales-ledger' && <LedgerPage type="sale" toast={toast} />}
          {view === 'purchase-ledger' && <LedgerPage type="purchase" toast={toast} />}
        </div>
      </div>

      {showSettings && (
        <SettingsModal
          settings={settings}
          onSave={s => { setSettings(s); loadStats(); }}
          onClose={() => setShowSettings(false)}
        />
      )}
      {showUpload && (
        <UploadModal
          type={tab}
          toast={toast}
          onDone={() => { loadInvoices(); loadStats(); }}
          onClose={() => setShowUpload(false)}
        />
      )}
      {showDriveScan && (
        <DriveScanModal
          type={tab}
          toast={toast}
          onDone={() => { loadInvoices(); loadStats(); }}
          onClose={() => setShowDriveScan(false)}
        />
      )}

      <ToastStack toasts={toasts} onRemove={removeToast} />
    </div>
  );
}